import * as React from 'react';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import clsx from 'clsx';

// Use default imports for Heading and Text
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import { IProps } from '@/components/Accordion/Accordion.interface';
import '@/components/Accordion/Accordion.styles.css'; // Import BEM-updated styles

const Accordion: React.FC<IProps> = ({
    items,
    type = 'single',
    defaultValue, // Used for type='single'
    defaultValues, // Used for type='multiple'
    collapsible = true, // Used for type='single'
}) => {
    // Separate props based on type for RadixAccordion.Root
    const commonProps = {
        className: 'accordion', // Use BEM block class
    };

    const rootProps =
        type === 'multiple'
            ? {
                ...commonProps,
                type: 'multiple' as const,
                defaultValue: defaultValues,
            }
            : {
                ...commonProps,
                type: 'single' as const,
                defaultValue: defaultValue,
                collapsible: collapsible,
            };

    return (
        <div>
            {/* Pass the correctly typed props */}
            <RadixAccordion.Root {...rootProps}>
                {items?.map((item, index) => (
                    <RadixAccordion.Item
                        key={`accordion-${item.value}-${index}`}
                        className="accordion__item" // Use BEM element class
                        value={item.value}
                    >
                        {/* Pass title to AccordionTrigger */}
                        <AccordionTrigger>{item.title}</AccordionTrigger>
                        {/* Pass content to AccordionContent */}
                        <AccordionContent>{item.content}</AccordionContent>
                    </RadixAccordion.Item>
                ))}
            </RadixAccordion.Root>
        </div>
    );
};

const AccordionRoot = RadixAccordion.Root;
const AccordionItem = RadixAccordion.Item;

// eslint-disable-next-line react/display-name
const AccordionTrigger = React.forwardRef<
    HTMLButtonElement,
    React.ComponentPropsWithoutRef<typeof RadixAccordion.Trigger>
>(({ children, className, ...props }, forwardedRef) => (
    // Apply header class to the Heading element
    <Heading level={3} className="accordion__header">
        <RadixAccordion.Trigger
            // Use clsx for trigger class and any passed className
            className={clsx('accordion__trigger', className)}
            {...props}
            ref={forwardedRef}
        >
            <Text as='span' html={children} className='accordion__trigger--text' />
            {/* Apply chevron class */}
            <ChevronDownIcon className="accordion__chevron" aria-hidden />
        </RadixAccordion.Trigger>
    </Heading>
));

// eslint-disable-next-line react/display-name
const AccordionContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<typeof RadixAccordion.Content>
>(({ children, className, ...props }, forwardedRef) => (
    <RadixAccordion.Content
        // Keep panel body in the DOM when closed so copy is available to site search,
        // crawlers, and find-in-page (Radix otherwise unmounts children and sets `hidden`).
        forceMount
        // Use clsx for content class and any passed className
        className={clsx('accordion__content', className)}
        {...props}
        ref={forwardedRef}
    >
        {/* Apply content-text class to the Text element */}
        {children ? typeof children === 'string' ? <Text className="accordion__content-text" html={children} /> : children : null}
    </RadixAccordion.Content>
));

export default Accordion;

export {
    AccordionRoot,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
};

